// ============================================
// Chat Page
// ============================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useChildrenStore, useChatStore, useUIStore } from '../store';
import { childrenAPI, chatAPI, voiceAPI } from '../services/api';
import { PersonaId, Child } from '../types';
import {
  Send,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  User,
  ChevronLeft,
  Settings,
  LogOut,
  Plus,
} from 'lucide-react';

// ============================================
// Persona Selector Component
// ============================================

function PersonaSelector({
  onSelect,
  selectedChild,
}: {
  onSelect: (persona: PersonaId) => void;
  selectedChild: Child | null;
}) {
  const personas = [
    {
      id: 'noor' as PersonaId,
      name: 'Noor老师',
      nameAr: 'نور',
      role: '古兰经老师',
      color: 'from-blue-400 to-blue-600',
      emoji: '📚',
      description: '温柔耐心，教你古兰经和阿拉伯语',
    },
    {
      id: 'iman' as PersonaId,
      name: 'Iman小伙伴',
      nameAr: 'إيمان',
      role: '讲故事的朋友',
      color: 'from-green-400 to-green-600',
      emoji: '✨',
      description: '活泼有趣，讲精彩的先知故事',
    },
    {
      id: 'hakeem' as PersonaId,
      name: 'Hakeem爷爷',
      nameAr: 'حكيم',
      role: '智慧长者',
      color: 'from-purple-400 to-purple-600',
      emoji: '🌙',
      description: '睿智慈祥，分享历史和人生智慧',
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
      <h2 className="text-2xl font-bold mb-2">
        {selectedChild ? `${selectedChild.nickname || selectedChild.name}，` : ''}选择你的AI朋友
      </h2>
      <p className="text-gray-600 mb-8">今天想和谁聊天呢？</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl w-full">
        {personas.map((persona) => (
          <button
            key={persona.id}
            onClick={() => onSelect(persona.id)}
            className={`persona-card bg-gradient-to-br ${persona.color} text-white text-left`}
          >
            <div className="text-5xl mb-4">{persona.emoji}</div>
            <h3 className="text-xl font-bold mb-1">{persona.name}</h3>
            <p className="text-sm opacity-80 font-arabic">{persona.nameAr}</p>
            <p className="text-sm opacity-90 mt-2">{persona.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Child Selector Component
// ============================================

function ChildSelector({
  children,
  onSelect,
  onAddChild,
}: {
  children: Child[];
  onSelect: (child: Child) => void;
  onAddChild: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
      <h2 className="text-2xl font-bold mb-2">谁在使用？</h2>
      <p className="text-gray-600 mb-8">选择孩子的档案</p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl w-full">
        {children.map((child) => (
          <button
            key={child.id}
            onClick={() => onSelect(child)}
            className="card hover:shadow-xl transition-all duration-200 hover:scale-105"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-green-400 mx-auto mb-3 flex items-center justify-center text-white text-2xl font-bold">
              {(child.nickname || child.name)[0]}
            </div>
            <h3 className="font-bold text-center">{child.nickname || child.name}</h3>
            <p className="text-sm text-gray-500 text-center">{child.ageGroup}岁</p>
          </button>
        ))}

        <button
          onClick={onAddChild}
          className="card hover:shadow-xl transition-all duration-200 hover:scale-105 border-2 border-dashed border-gray-300"
        >
          <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto mb-3 flex items-center justify-center">
            <Plus className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-medium text-center text-gray-500">添加孩子</h3>
        </button>
      </div>
    </div>
  );
}

// ============================================
// Chat Window Component
// ============================================

function ChatWindow() {
  const { currentChild, setCurrentChild } = useChildrenStore();
  const { currentPersona, sessionId, messages, isLoading, setPersona, setSessionId, addMessage, setLoading, resetChat } =
    useChatStore();
  const { isVoiceMode, isRecording, setVoiceMode, setRecording } = useUIStore();

  const [inputText, setInputText] = useState('');
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputText.trim() || !currentChild || !currentPersona || isLoading) return;

    const userMessage = inputText.trim();
    setInputText('');
    setLoading(true);

    // Add user message immediately
    addMessage({
      id: Date.now().toString(),
      role: 'user',
      content: userMessage,
      timestamp: Date.now(),
    });

    try {
      const response = await chatAPI.sendMessage(
        currentChild.id,
        currentPersona,
        userMessage,
        sessionId || undefined
      );

      setSessionId(response.sessionId);

      // Add assistant message
      addMessage({
        id: Date.now().toString() + '-assistant',
        role: 'assistant',
        content: response.message,
        audioUrl: response.audioUrl,
        timestamp: Date.now(),
      });

      // Auto-play TTS if voice mode is on
      if (isVoiceMode && response.audioUrl) {
        playAudio(response.audioUrl);
      }
    } catch (error: any) {
      console.error('Send message error:', error);
      addMessage({
        id: Date.now().toString() + '-error',
        role: 'assistant',
        content: '抱歉，出了点问题。请再试一次。',
        timestamp: Date.now(),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const playAudio = async (audioUrl: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    setIsPlayingAudio(true);
    audio.onended = () => setIsPlayingAudio(false);
    audio.play().catch(console.error);
  };

  const handleBack = () => {
    if (sessionId) {
      chatAPI.endSession(sessionId).catch(console.error);
    }
    resetChat();
  };

  const getPersonaInfo = (id: PersonaId) => {
    const personas: Record<PersonaId, { name: string; color: string; emoji: string }> = {
      noor: { name: 'Noor老师', color: 'bg-blue-500', emoji: '📚' },
      iman: { name: 'Iman', color: 'bg-green-500', emoji: '✨' },
      hakeem: { name: 'Hakeem爷爷', color: 'bg-purple-500', emoji: '🌙' },
    };
    return personas[id];
  };

  const personaInfo = currentPersona ? getPersonaInfo(currentPersona) : null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          {personaInfo && (
            <div className="flex items-center gap-2">
              <div
                className={`w-10 h-10 rounded-full ${personaInfo.color} flex items-center justify-center text-white text-lg`}
              >
                {personaInfo.emoji}
              </div>
              <div>
                <h3 className="font-bold">{personaInfo.name}</h3>
                <p className="text-xs text-gray-500">与 {currentChild?.nickname || currentChild?.name} 对话中</p>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setVoiceMode(!isVoiceMode)}
            className={`p-2 rounded-full transition-colors ${isVoiceMode ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
            title={isVoiceMode ? '关闭语音模式' : '开启语音模式'}
          >
            {isVoiceMode ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <p>开始和 {personaInfo?.name} 聊天吧！</p>
            <p className="text-sm mt-2">你可以问问题、听故事、或者学习古兰经</p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className="max-w-[80%]">
              <div
                className={`chat-bubble ${message.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-assistant'}`}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
                {message.audioUrl && (
                  <button
                    onClick={() => playAudio(message.audioUrl!)}
                    className="mt-2 text-sm text-blue-500 hover:underline flex items-center gap-1"
                  >
                    <Volume2 className="w-4 h-4" />
                    播放语音
                  </button>
                )}
              </div>
              <p className={`text-xs text-gray-400 mt-1 ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                {new Date(message.timestamp || Date.now()).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="chat-bubble chat-bubble-assistant">
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-white border-t p-4">
        <div className="flex items-center gap-3 max-w-4xl mx-auto">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="输入消息..."
            className="input-field flex-1"
            disabled={isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isLoading}
            className="btn-primary px-4"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} hidden />
    </div>
  );
}

// ============================================
// Main Chat Page
// ============================================

export default function ChatPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { children, currentChild, setChildren, setCurrentChild } = useChildrenStore();
  const { currentPersona, setPersona, resetChat } = useChatStore();

  const [showAddChild, setShowAddChild] = useState(false);
  const [newChildName, setNewChildName] = useState('');
  const [newChildAge, setNewChildAge] = useState<'3-5' | '6-8' | '9-12'>('6-8');
  const [isLoadingChildren, setIsLoadingChildren] = useState(true);

  useEffect(() => {
    loadChildren();
  }, []);

  const loadChildren = async () => {
    try {
      const { children } = await childrenAPI.getAll();
      setChildren(children);
    } catch (error) {
      console.error('Load children error:', error);
    } finally {
      setIsLoadingChildren(false);
    }
  };

  const handleAddChild = async () => {
    if (!newChildName.trim()) return;

    try {
      const { child } = await childrenAPI.create({
        name: newChildName,
        ageGroup: newChildAge,
      });
      setChildren([...children, child]);
      setShowAddChild(false);
      setNewChildName('');
      setCurrentChild(child);
    } catch (error) {
      console.error('Add child error:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (isLoadingChildren) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white shadow-sm p-3 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center text-white font-bold text-sm">
            E
          </div>
          <span className="font-bold text-gray-800">Equantu Kids</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/parent')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="家长面板"
          >
            <Settings className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={handleLogout}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="退出"
          >
            <LogOut className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {!currentChild ? (
          <ChildSelector
            children={children}
            onSelect={setCurrentChild}
            onAddChild={() => setShowAddChild(true)}
          />
        ) : !currentPersona ? (
          <PersonaSelector onSelect={setPersona} selectedChild={currentChild} />
        ) : (
          <ChatWindow />
        )}
      </div>

      {/* Add Child Modal */}
      {showAddChild && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="card max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">添加孩子档案</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">孩子的名字</label>
                <input
                  type="text"
                  value={newChildName}
                  onChange={(e) => setNewChildName(e.target.value)}
                  className="input-field"
                  placeholder="输入名字"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">年龄段</label>
                <div className="flex gap-2">
                  {(['3-5', '6-8', '9-12'] as const).map((age) => (
                    <button
                      key={age}
                      onClick={() => setNewChildAge(age)}
                      className={`flex-1 py-2 rounded-lg border-2 transition-colors ${
                        newChildAge === age
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {age}岁
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAddChild(false)} className="btn-secondary flex-1">
                取消
              </button>
              <button onClick={handleAddChild} className="btn-primary flex-1">
                添加
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
